import {
  Issue,
  IssueCreationSource,
  IssueMessageType,
  Message,
  useCurrentAccount,
} from '@gripp/shared-logic';
import * as Haptics from 'expo-haptics';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { Text } from 'react-native-paper';
import { runOnJS, useSharedValue } from 'react-native-reanimated';
import { Colors } from '../../themes/colors';
import { AccountAvatar } from '../accountAvatar';
import { Avatar } from '../crop-circle/Avatar';
import { Icon } from '../crop-circle/icons';
import { MessageActionMenu } from './messageActionMenu';
import { Link } from '../crop-circle/Link/Link';
import { tokens } from '../crop-circle/tokens';
import { LocationText } from '../locationText';
import { AccessRequestMessage } from './accessRequestMessage';
import {
  AnonymousId,
  FunctionId,
  getIssueFullName,
  getIssueMessageHeader,
  getIssueMessageTextStyle,
} from './issueFormatUtils';
import { NewReaction } from './reaction/newReaction';
import { ReactionList } from './reaction/reactionList';
import { ReminderMessage } from './reminderMessage';
import { RoutineExceptionMessage } from './routineExceptionMessage';
import { RoutineMessage } from './routineMessage';
import { RoutinePromptMessage } from './routinePromptMessage';
import { MessageComponent } from './types';
import { UserMessage } from './userMessage';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type IssueMessageProps = {
  message: Message;
  mediaUrl: string;
  maxImageWidth?: number;
  isUnread: boolean;
  issue: Issue;
  isFirstMessage: boolean;
  onImagePress?: (mediaId: string) => void;
  /**
   * When provided AND the message author is a real, callable account (not the
   * current user, not the anonymous/system accounts), an overflow (⋯) menu with
   * a "Call" action appears next to the author. Gated upstream by `call.create`.
   */
  onCall?: (actor: { id: string; name: string }) => void;
  /**
   * Side Chat (plan 0022). When provided, the per-message overflow (⋯) menu
   * reveals a "Side chat" row and — once a side chat exists — a "X replies ·
   * Side Chat" pill under the message body. Unlike Call, side chat is available
   * on ANY message (including the current user's own). Gated upstream by
   * `COMMUNICATION_PLATFORM` + `issuemessage.create` (passed as `undefined` when
   * the gate is off, which hides the row and the pill).
   */
  onOpenSideChat?: (message: Message) => void;
  /**
   * Rows inside a thread or side chat offer Call | Copy only — this is that
   * Copy: copies the message's own text to the clipboard. The
   * caller owns the actual clipboard write (platform-specific); this only
   * decides whether the row offers it. Hidden when the message has no text
   * of its own (media-only) — there is nothing to copy.
   */
  onCopy?: (message: Message) => void;
  /** See MessageThread's own prop — initials avatars for the chat surface. */
  useInitialsAvatar?: boolean;
  /**
   * Per-origin side-chat summary for THIS message (from `useSideChatSummaries`).
   * When `exists` is true the "X replies · Side Chat" pill renders under the
   * body. `undefined` means no side chat has been started from this message.
   */
  sideChatSummary?: { exists: boolean; replyCount: number };
  /**
   * End-to-end calling demo (plan 0021 addendum). When true AND the message
   * is the first message ("Call started") AND `onJoinCall` is provided, a
   * "Call in progress · Join" pill renders under the message body. Driven by
   * `useActiveCallForIssue`; gated upstream by `COMMUNICATION_PLATFORM` +
   * `call.create`.
   */
  callInProgress?: boolean;
  /** Opens/joins the Activity's group call. Paired with `callInProgress`. */
  onJoinCall?: () => void;
  /**
   * Supplied only while the viewer is signed out on a share link. Renders the
   * "Anonymous (<sharer>'s Guest)" author label as a link that starts sign-up,
   * so the viewer can claim those messages. There is no "own message" check:
   * an anonymous viewer has no identity to compare against, so every guest
   * message in the thread is offered — the claim is per conversation anyway.
   */
  onAnonymousAuthorPress?: () => void;
};

const MessageComponents: Record<IssueMessageType, MessageComponent> = {
  [IssueMessageType.AccessRequestMessage]: AccessRequestMessage,
  [IssueMessageType.AccessRequestResolved]: UserMessage,
  [IssueMessageType.UserMessage]: UserMessage,
  [IssueMessageType.SystemMessage]: UserMessage,
  [IssueMessageType.SystemMessageReminderAlert]: UserMessage,
  [IssueMessageType.ReminderMessage]: ReminderMessage,
  [IssueMessageType.RoutineMessage]: RoutineMessage,
  [IssueMessageType.RoutinePromptMessage]: RoutinePromptMessage,
  [IssueMessageType.RoutineExceptionMessage]: RoutineExceptionMessage,
};

const UNREAD_HIGHLIGHT_TIME_MS = 3000;

/** 1-2 letter initials, same rule the chat feed's rows use. */
const getMessageInitials = (name: string): string =>
  name
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

export const IssueMessage = (props: IssueMessageProps) => {
  const MessageComponent = MessageComponents[props.message.type];
  const [reactionVisible, setReactionVisible] = useState(false);
  const anchorRef = useRef<View>(null);

  const maxImageWidth = props.maxImageWidth || 320;
  const imageDimensions = {
    width: maxImageWidth,
    height: maxImageWidth,
  };
  const message = props.message;
  const issue = props.issue;
  const header = getIssueMessageHeader(message, issue);
  const textStyle = getIssueMessageTextStyle(message);
  const { account: currentAccount } = useCurrentAccount();
  const { t } = useTranslation();
  const authorLabel = header.updatedByKey
    ? t(header.updatedByKey, header.updatedByParams)
    : header.updatedBy;
  const canClaimAuthor =
    !!header.isGuestMessage && !!props.onAnonymousAuthorPress;
  // Reactions read the reacting accounts and write as the current account;
  // neither is available to a signed-out viewer.
  const showReactions = !!currentAccount;

  // Per-message call affordance: only for a real, callable author that isn't the
  // current user or the anonymous/system accounts. Gated upstream by call.create
  // (the screen only passes `onCall` when the caller may place calls).
  const author = message.updatedBy;
  const callActor =
    props.onCall &&
    author?.id &&
    ![AnonymousId, FunctionId].includes(author.id) &&
    author.id !== currentAccount?.id
      ? {
          id: author.id,
          name: getIssueFullName(author.id, author.name ?? undefined),
        }
      : undefined;

  // Copy has nothing to act on for a media-only message.
  const copyAction =
    props.onCopy && message.text ? () => props.onCopy?.(message) : undefined;

  const [unreadStyle, setUnreadStyle] = useState(props.isUnread);
  // Web-only hover, tracked at the message-row level so the call affordance
  // can reveal itself the way it does in Gripp OS.
  const [hovered, setHovered] = useState(false);
  const hoverProps =
    Platform.OS === 'web'
      ? {
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        }
      : {};

  useEffect(() => {
    if (props.isUnread) {
      setTimeout(() => {
        setUnreadStyle(false);
      }, UNREAD_HIGHLIGHT_TIME_MS);
    }
  }, []);

  const isTouchingReactions = useSharedValue(false);

  const handleLongPress = () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setReactionVisible(true);
  };

  const longPressGesture = Gesture.LongPress()
    .minDuration(500)
    .enabled(Platform.OS !== 'web')
    .onStart(() => {
      if (!isTouchingReactions.value) {
        runOnJS(handleLongPress)();
      }
      isTouchingReactions.value = false;
    });

  const showCreatedFrom =
    (issue.createdFrom === IssueCreationSource.Scanner ||
      issue.createdFrom === IssueCreationSource.Anonymousweb) &&
    props.isFirstMessage;

  const messageContent = (
    <View
      ref={anchorRef}
      style={[styles.container, unreadStyle ? styles.unread : styles.read]}
      {...hoverProps}
    >
      <View style={styles.titleRow}>
        {props.useInitialsAvatar ? (
          // The chat surface uses initials on every row, matching the asset
          // chat feed and the gripp-os reference. AccountAvatar falls back to
          // a generic `account-circle` for anyone without a photo, which read
          // as a different product sitting under an initials-avatar feed.
          <Avatar
            type="initial"
            size="medium"
            shape="rounded"
            tone="muted"
            text={getMessageInitials(authorLabel)}
          />
        ) : (
          <AccountAvatar
            avatar={header.avatar}
            avatarIcon={header.avatarIcon}
            mediaUrl={props.mediaUrl}
            size={32}
            style={header.avatarIcon ? header.avatarIconStyle : {}}
            iconColor={header.avatarIconColor}
            hasLightbox
          />
        )}
        <View style={styles.nameArea}>
          {canClaimAuthor ? (
            <Link
              color="inherit"
              onPress={props.onAnonymousAuthorPress}
              accessibilityRole="button"
              accessibilityLabel={t('issue.message.anonymousGuestSignInA11y')}
              testID={`message-author-claim-${message.id}`}
              textStyle={StyleSheet.flatten([styles.name, header.fontStyle])}
            >
              {authorLabel}
            </Link>
          ) : (
            <Text style={[styles.name, header.fontStyle]} numberOfLines={1}>
              {authorLabel}
            </Text>
          )}
        </View>
        <Text style={styles.time}>{header.updatedAt}</Text>
        {(callActor || props.onOpenSideChat || copyAction) && (
          <MessageActionMenu
            hovered={hovered}
            messageName={authorLabel}
            onSideChat={
              props.onOpenSideChat
                ? () => props.onOpenSideChat?.(message)
                : undefined
            }
            onCall={
              callActor && props.onCall
                ? () => props.onCall?.(callActor)
                : undefined
            }
            onCopy={copyAction}
          />
        )}
      </View>
      {showCreatedFrom && (
        <LocationText
          location={issue.location}
          address={issue.createdFromAddress ?? ''}
        />
      )}
      <View style={styles.message}>
        <MessageComponent
          message={message}
          imageDimensions={imageDimensions}
          mediaUrl={props.mediaUrl}
          textStyle={textStyle}
          issue={props.issue}
          onImagePress={props.onImagePress}
        />
        {!showReactions ? null : Platform.OS === 'android' ? (
          <View
            onTouchStart={() => {
              isTouchingReactions.value = true;
            }}
          >
            <ReactionList
              onOpenNewReaction={() => setReactionVisible(true)}
              issue={props.issue}
              message={message}
            />
          </View>
        ) : (
          <ReactionList
            onOpenNewReaction={() => setReactionVisible(true)}
            issue={props.issue}
            message={message}
          />
        )}
      </View>
      {props.sideChatSummary?.exists && props.onOpenSideChat && (
        <SideChatPill
          replyCount={props.sideChatSummary.replyCount}
          messageId={message.id}
          onPress={() => props.onOpenSideChat?.(message)}
        />
      )}
      {props.isFirstMessage && props.callInProgress && props.onJoinCall && (
        <CallInProgressPill onPress={props.onJoinCall} />
      )}
    </View>
  );

  const newReaction = showReactions && (
    <NewReaction
      onCloseReaction={() => setReactionVisible(false)}
      isReactionVisible={reactionVisible}
      issue={props.issue}
      message={message}
      anchorRef={anchorRef as React.RefObject<View>}
    />
  );

  if (Platform.OS === 'android') {
    return (
      <>
        <GestureDetector gesture={longPressGesture}>
          {messageContent}
        </GestureDetector>
        {newReaction}
      </>
    );
  }

  if (Platform.OS === 'ios') {
    return (
      <>
        <TouchableOpacity activeOpacity={1} onLongPress={handleLongPress}>
          {messageContent}
        </TouchableOpacity>
        {newReaction}
      </>
    );
  }

  return (
    <>
      {messageContent}
      {newReaction}
    </>
  );
};

/**
 * "X replies · Side Chat" pill (plan 0022, ported from gripp-os
 * SubConversationIndicator). A gray rounded pill under the message body: orange
 * comment icon + orange reply count, " · Side Chat" in soft foreground, and a
 * trailing chevron. Tapping it opens the message's Side Chat panel.
 */
const SideChatPill = ({
  replyCount,
  messageId,
  onPress,
}: {
  replyCount: number;
  messageId: string;
  onPress: () => void;
}) => {
  const replyLabel = `${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`;
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`${replyLabel} · Side Chat`}
      testID={`message-sidechat-pill-${messageId}`}
      style={({ pressed }) => [
        styles.sideChatPill,
        pressed && styles.sideChatPillPressed,
      ]}
    >
      <Icon name="Comment" size={16} color={tokens.colors.brand.core} />
      <Text style={styles.sideChatCount}>{replyLabel}</Text>
      <Text style={styles.sideChatLabel}>· Side Chat</Text>
      <View style={styles.sideChatChevron}>
        <Icon
          name="Chevron Right"
          size={12}
          color={Colors.role.textSecondary}
        />
      </View>
    </Pressable>
  );
};

/**
 * "Call in progress · Join" pill (end-to-end calling demo, plan 0021
 * addendum). Rendered under the body of the first message ("Call started")
 * while `useActiveCallForIssue` reports an active group call. Styled like
 * `SideChatPill` — a gray rounded chip — but with the `Call` (handset) icon
 * and no reply count. Tapping it opens/joins the group call.
 */
const CallInProgressPill = ({ onPress }: { onPress: () => void }) => {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel="Call in progress · Join"
      style={({ pressed }) => [
        styles.sideChatPill,
        pressed && styles.sideChatPillPressed,
      ]}
    >
      <Icon name="Call" size={16} color={tokens.colors.brand.core} />
      <Text style={styles.sideChatCount}>Call in progress</Text>
      <Text style={styles.sideChatLabel}>· Join</Text>
      <View style={styles.sideChatChevron}>
        <Icon
          name="Chevron Right"
          size={12}
          color={Colors.role.textSecondary}
        />
      </View>
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    flexDirection: 'column',
    padding: 16,
    gap: 8,
  },
  // "X replies · Side Chat" pill — gray rounded chip aligned under the message
  // body (avatar 32 + row gap 8 = 40 left inset).
  sideChatPill: {
    marginLeft: 40,
    marginRight: 16,
    marginTop: 2,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: tokens.borderRadius.lg,
    backgroundColor: role.surfaceHover,
  },
  sideChatPillPressed: {
    backgroundColor: role.surfaceActive,
  },
  sideChatCount: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
    color: role.brandOnSurface,
  },
  sideChatLabel: {
    fontSize: 12,
    lineHeight: 16,
    color: role.textPrimary,
  },
  sideChatChevron: {
    marginLeft: 4,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    opacity: Platform.OS === 'web' ? 0.9 : 0.8,
  },
  nameArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  name: {
    fontWeight: '700',
    fontSize: 14,
    lineHeight: 18,
    color: role.textStrong,
  },
  time: {
    color: role.textSoft,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
  },
  message: {
    marginTop: 0,
  },
  messageText: {
    flexWrap: 'wrap',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: Platform.OS === 'web' ? 18 : 22,
    color: role.textStrong,
  },
  // Web's chat sits flat on the panel; a raised row read as a band across it.
  read: {
    backgroundColor: Platform.OS === 'web' ? role.canvas : role.surface,
  },
  unread: {
    backgroundColor: Colors.unreadBackground,
  },
  modalContainer: {
    backgroundColor: 'transparent',
  },
  modalContentContainer: {
    marginTop: 'auto',
    height: 500,
  },
}));
